/* eslint-disable @typescript-eslint/no-deprecated */
import { z } from 'zod/v4';
import { MiniBannerTargetType, PromoAnnouncementEventType } from '@masters-club/types';
import { paginationSchema, stringToBooleanSchema, type PaginationInput } from '../common/index';
import { segmentDefinitionSchema } from './audience-segment';

// ==================== Common Schemas ====================

export const translationBaseSchema = z.object({
  languageCode: z.string().length(2, 'Код языка должен быть 2 символа'),
});

export const reorderSchema = z.object({
  ids: z.array(z.number().int()),
});

// Re-export pagination for backward compatibility
export type PaginationDto = PaginationInput;

// ==================== Banner Schemas ====================
// Баннер — это изображение + ссылка. Без текстовых полей и без layout-позиционирования.

export const aspectRatioSchema = z.string()
  .regex(/^\d+:\d+$/, 'Aspect ratio должен быть в формате "16:9", "3:1" и т.д.');

export const bannerImageInputSchema = z.object({
  imageId: z.number().int('ID изображения должен быть целым числом').positive('ID изображения должен быть положительным'),
  aspectRatio: aspectRatioSchema,
  languageCode: z.string().length(2, 'Код языка должен быть 2 символа').nullable().optional(),
  type: z.enum(['background', 'logo']).default('background'),
  order: z.number().int('Порядок должен быть целым числом').default(0),
});

export const createBannerSchema = z.object({
  images: z.array(bannerImageInputSchema).min(1, 'Требуется хотя бы одно изображение'),
  linkUrl: z.string().url('Некорректный URL ссылки').nullable().optional(),
  order: z.number().int('Порядок должен быть целым числом').default(0),
  startDate: z.coerce.date().nullable().optional(),
  endDate: z.coerce.date().nullable().optional(),
  isActive: z.boolean().default(true),
});

export const updateBannerSchema = createBannerSchema.partial();

export const bannerFiltersSchema = paginationSchema.extend({
  isActive: stringToBooleanSchema,
  startDateFrom: z.coerce.date().optional(),
  startDateTo: z.coerce.date().optional(),
  endDateFrom: z.coerce.date().optional(),
  endDateTo: z.coerce.date().optional(),
  search: z.string().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.string().optional(),
});

export const toggleBannerSchema = z.object({
  isActive: z.boolean(),
});

// ==================== Promo Announcement Schemas ====================
// Анонс — fullscreen-промо при входе на платформу. Один активный по приоритету (order).
// Картинка + ссылка (как баннер) + таргетинг по аудитории (audienceSegment) + теги + язык.
// Аналитика: показ (IMPRESSION) / переход (CLICK) / закрытие (CLOSE).

/**
 * Адаптивные слоты анонса под девайсы. Гарантируют, что контент не перекрывается
 * на разных экранах — три отдельных изображения, по одному на форм-фактор.
 *  - 16:9 — десктоп (>= 1024px).
 *  - 4:3  — планшет (>= 640px).
 *  - 9:16 — мобильный (< 640px).
 */
export const ANNOUNCEMENT_DESKTOP_ASPECT_RATIO = '16:9';
export const ANNOUNCEMENT_TABLET_ASPECT_RATIO = '4:3';
export const ANNOUNCEMENT_MOBILE_ASPECT_RATIO = '9:16';

export const ANNOUNCEMENT_DEVICE_SLOTS = [
  {
    aspectRatio: ANNOUNCEMENT_DESKTOP_ASPECT_RATIO,
    label: 'Десктоп',
    description: '16:9, рекомендуемый размер 1920×1080',
    required: true,
  },
  {
    aspectRatio: ANNOUNCEMENT_TABLET_ASPECT_RATIO,
    label: 'Планшет',
    description: '4:3, рекомендуемый размер 1536×1152',
    required: false,
  },
  {
    aspectRatio: ANNOUNCEMENT_MOBILE_ASPECT_RATIO,
    label: 'Мобильный',
    description: '9:16, рекомендуемый размер 1080×1920',
    required: false,
  },
] as const;

export const announcementImageInputSchema = z.object({
  imageId: z.number().int('ID изображения должен быть целым числом').positive('ID изображения должен быть положительным'),
  aspectRatio: aspectRatioSchema.default(ANNOUNCEMENT_DESKTOP_ASPECT_RATIO),
  languageCode: z.string().length(2, 'Код языка должен быть 2 символа').nullable().optional(),
  type: z.enum(['background', 'logo']).default('background'),
  order: z.number().int('Порядок должен быть целым числом').default(0),
});

export const createAnnouncementSchema = z.object({
  images: z.array(announcementImageInputSchema).min(1, 'Требуется хотя бы одно изображение'),
  linkUrl: z.string().url('Некорректный URL ссылки').nullable().optional(),
  // Таргетинг по аудитории. null/пустой groups = виден всем (после фильтра тегов/языка).
  audienceSegment: segmentDefinitionSchema.nullable().optional(),
  order: z.number().int('Порядок должен быть целым числом').default(0),
  startDate: z.coerce.date().nullable().optional(),
  endDate: z.coerce.date().nullable().optional(),
  isActive: z.boolean().default(true),
});

export const updateAnnouncementSchema = createAnnouncementSchema.partial();

export const announcementFiltersSchema = paginationSchema.extend({
  isActive: stringToBooleanSchema,
  startDateFrom: z.coerce.date().optional(),
  startDateTo: z.coerce.date().optional(),
  endDateFrom: z.coerce.date().optional(),
  endDateTo: z.coerce.date().optional(),
  search: z.string().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.string().optional(),
});

export const toggleAnnouncementSchema = z.object({
  isActive: z.boolean(),
});

// Трекинг события показа/закрытия с публичной стороны (CLICK трекается через dismiss/переход).
export const announcementEventInputSchema = z.object({
  type: z.enum([
    PromoAnnouncementEventType.IMPRESSION,
    PromoAnnouncementEventType.CLICK,
    PromoAnnouncementEventType.CLOSE,
  ] as const),
});

// Фильтр списка событий в админ-аналитике.
export const announcementEventsQuerySchema = paginationSchema.extend({
  type: z.enum([
    PromoAnnouncementEventType.IMPRESSION,
    PromoAnnouncementEventType.CLICK,
    PromoAnnouncementEventType.CLOSE,
  ] as const).optional(),
});

// ==================== Mini Banner Schemas ====================
// Мини-баннер показывается в карточке урока между контентом и блоком
// навигации "Следующий урок". Привязан к курсу/модулю/уроку через
// miniBannerTarget.
//
// Локализация:
//  - картинки — через languageCode на MiniBannerImage (null = универсальная)
//  - тексты (title/subtitle/buttonText) — через универсальную таблицу Translation
//    с entityType='MINI_BANNER'

/**
 * Адаптивные слоты мини-баннера.
 *  - 19:2 — десктоп/широкие экраны (>= 640px), обязателен.
 *  - 21:9 — мобильные (< 640px), опционален; при отсутствии fallback на 19:2.
 */
export const MINI_BANNER_DESKTOP_ASPECT_RATIO = '19:2';
export const MINI_BANNER_MOBILE_ASPECT_RATIO = '21:9';

export const MINI_BANNER_DEVICE_SLOTS = [
  {
    aspectRatio: MINI_BANNER_DESKTOP_ASPECT_RATIO,
    label: 'Десктоп',
    description: '19:2, рекомендуемый размер 1900×200',
    required: true,
  },
  {
    aspectRatio: MINI_BANNER_MOBILE_ASPECT_RATIO,
    label: 'Мобильный',
    description: '21:9, рекомендуемый размер 1080×463',
    required: false,
  },
] as const;

export const miniBannerImageInputSchema = z.object({
  imageId: z.number().int('ID изображения должен быть целым числом').positive('ID изображения должен быть положительным'),
  // '19:2' — десктоп (дефолт для обратной совместимости), '21:9' — мобильный.
  // Тот же aspectRatioSchema, что у обычного Banner — единый паттерн проекта.
  aspectRatio: aspectRatioSchema.default(MINI_BANNER_DESKTOP_ASPECT_RATIO),
  languageCode: z.string().length(2, 'Код языка должен быть 2 символа').nullable().optional(),
});

export const miniBannerTargetInputSchema = z.object({
  targetType: z.enum([
    MiniBannerTargetType.COURSE,
    MiniBannerTargetType.MODULE,
    MiniBannerTargetType.MODULE_ITEM,
    MiniBannerTargetType.LIVESTREAM,
  ] as const, {
    error: 'Тип цели должен быть COURSE, MODULE, MODULE_ITEM или LIVESTREAM',
  }),
  targetId: z.number().int('ID цели должен быть целым числом').positive('ID цели должен быть положительным'),
});

export const miniBannerTranslationSchema = z.object({
  languageCode: z.string().length(2, 'Код языка должен быть 2 символа'),
  title: z.string().max(200, 'Заголовок слишком длинный (максимум 200 символов)').nullable().optional(),
  subtitle: z.string().max(500, 'Подзаголовок слишком длинный (максимум 500 символов)').nullable().optional(),
  buttonText: z.string().max(50, 'Текст кнопки слишком длинный (максимум 50 символов)').nullable().optional(),
});

export const createMiniBannerSchema = z.object({
  images: z.array(miniBannerImageInputSchema).min(1, 'Требуется хотя бы одно изображение'),
  targets: z.array(miniBannerTargetInputSchema).min(1, 'Требуется хотя бы одна цель'),
  translations: z.array(miniBannerTranslationSchema).default([]),
  linkUrl: z.string().url('Некорректный URL ссылки').nullable().optional(),
  order: z.number().int('Порядок должен быть целым числом').default(0),
  startDate: z.coerce.date().nullable().optional(),
  endDate: z.coerce.date().nullable().optional(),
  isActive: z.boolean().default(true),
});

export const updateMiniBannerSchema = createMiniBannerSchema.partial();

export const miniBannerFiltersSchema = paginationSchema.extend({
  isActive: stringToBooleanSchema,
  targetType: z.enum([
    MiniBannerTargetType.COURSE,
    MiniBannerTargetType.MODULE,
    MiniBannerTargetType.MODULE_ITEM,
    MiniBannerTargetType.LIVESTREAM,
  ] as const).optional(),
  targetId: z.coerce.number().int().positive().optional(),
  search: z.string().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.string().optional(),
});

export const toggleMiniBannerSchema = z.object({
  isActive: z.boolean(),
});

export const publicMiniBannerForLessonQuerySchema = z.object({
  lessonId: z.coerce.number().int('ID урока должен быть целым числом').positive('ID урока должен быть положительным'),
  limit: z.coerce.number().int().positive().max(10, 'Лимит должен быть <= 10').default(3),
});

// ==================== Teacher Schemas ====================

export const socialLinksSchema = z.record(z.string(), z.string()).nullable().optional();

export const teacherTranslationSchema = translationBaseSchema.extend({
  name: z.string()
    .min(1, 'Имя обязательно')
    .max(100, 'Имя слишком длинное (максимум 100 символов)'),
  role: z.string().max(100, 'Роль слишком длинная (максимум 100 символов)').nullable().optional(),
  bio: z.string().max(2000, 'Биография слишком длинная (максимум 2000 символов)').nullable().optional(),
});

export const createTeacherSchema = z.object({
  userId: z.number().int().nullable().optional(),
  photoUrl: z.string().url('Некорректный URL фото').nullable().optional(),
  photoId: z.number().int().nullable().optional(),
  order: z.number().int('Порядок должен быть целым числом').default(0),
  isActive: z.boolean().default(true),
  socialLinks: socialLinksSchema,
  translations: z.array(teacherTranslationSchema).min(1, 'Требуется хотя бы один перевод'),
});

export const updateTeacherSchema = createTeacherSchema.partial();

export const teacherFiltersSchema = paginationSchema.extend({
  isActive: stringToBooleanSchema,
});

export const toggleTeacherSchema = z.object({
  isActive: z.boolean(),
});

// ==================== Public API Schemas ====================

export const publicBannersQuerySchema = z.object({
  limit: z.coerce.number()
    .int('Лимит должен быть целым числом')
    .positive('Лимит должен быть положительным')
    .max(20, 'Лимит должен быть <= 20')
    .default(10),
});

export const publicTeachersQuerySchema = z.object({
  limit: z.coerce.number()
    .int('Лимит должен быть целым числом')
    .positive('Лимит должен быть положительным')
    .max(50, 'Лимит должен быть <= 50')
    .default(20),
});

// ==================== Home Page Schema ====================

export const homePageQuerySchema = z.object({
  bannersLimit: z.coerce.number()
    .int('Лимит должен быть целым числом')
    .positive('Лимит должен быть положительным')
    .max(10, 'Лимит баннеров должен быть <= 10')
    .default(5),
  coursesLimit: z.coerce.number()
    .int('Лимит должен быть целым числом')
    .positive('Лимит должен быть положительным')
    .max(10, 'Лимит курсов должен быть <= 10')
    .default(5),
});

// ==================== Type Exports ====================

export type BannerImageInputDto = z.infer<typeof bannerImageInputSchema>;
export type CreateBannerDto = z.infer<typeof createBannerSchema>;
export type UpdateBannerDto = z.infer<typeof updateBannerSchema>;
export type BannerFiltersDto = z.infer<typeof bannerFiltersSchema>;
export type ToggleBannerDto = z.infer<typeof toggleBannerSchema>;

export type AnnouncementImageInputDto = z.infer<typeof announcementImageInputSchema>;
export type CreateAnnouncementDto = z.infer<typeof createAnnouncementSchema>;
export type UpdateAnnouncementDto = z.infer<typeof updateAnnouncementSchema>;
export type AnnouncementFiltersDto = z.infer<typeof announcementFiltersSchema>;
export type ToggleAnnouncementDto = z.infer<typeof toggleAnnouncementSchema>;
export type AnnouncementEventInputDto = z.infer<typeof announcementEventInputSchema>;
export type AnnouncementEventsQueryDto = z.infer<typeof announcementEventsQuerySchema>;

export type MiniBannerImageInputDto = z.infer<typeof miniBannerImageInputSchema>;
export type MiniBannerTargetInputDto = z.infer<typeof miniBannerTargetInputSchema>;
export type MiniBannerTranslationDto = z.infer<typeof miniBannerTranslationSchema>;
export type CreateMiniBannerDto = z.infer<typeof createMiniBannerSchema>;
export type UpdateMiniBannerDto = z.infer<typeof updateMiniBannerSchema>;
export type MiniBannerFiltersDto = z.infer<typeof miniBannerFiltersSchema>;
export type ToggleMiniBannerDto = z.infer<typeof toggleMiniBannerSchema>;
export type PublicMiniBannerForLessonQueryDto = z.infer<typeof publicMiniBannerForLessonQuerySchema>;

export type SocialLinksDto = z.infer<typeof socialLinksSchema>;
export type TeacherTranslationDto = z.infer<typeof teacherTranslationSchema>;
export type CreateTeacherDto = z.infer<typeof createTeacherSchema>;
export type UpdateTeacherDto = z.infer<typeof updateTeacherSchema>;
export type TeacherFiltersDto = z.infer<typeof teacherFiltersSchema>;
export type ToggleTeacherDto = z.infer<typeof toggleTeacherSchema>;

export type ReorderDto = z.infer<typeof reorderSchema>;

export type PublicBannersQueryDto = z.infer<typeof publicBannersQuerySchema>;
export type PublicTeachersQueryDto = z.infer<typeof publicTeachersQuerySchema>;
export type HomePageQueryDto = z.infer<typeof homePageQuerySchema>;

// ==================== Response & Extended Types ====================

// Banner Service Types
export interface LocalizedBannerImage {
  id: number;
  imageUrl: string | null;
  aspectRatio: string;
  languageCode: string | null;
  type: string;
  order: number;
}

export interface LocalizedBanner {
  id: number;
  linkUrl: string | null;
  images: LocalizedBannerImage[];
}

// Promo Announcement Service Types
export interface LocalizedAnnouncementImage {
  id: number;
  imageUrl: string | null;
  aspectRatio: string;
  languageCode: string | null;
  type: string;
  order: number;
}

export interface LocalizedAnnouncement {
  id: number;
  linkUrl: string | null;
  images: LocalizedAnnouncementImage[];
}

// Mini Banner Service Types
export interface LocalizedMiniBannerImage {
  id: number;
  imageUrl: string | null;
  aspectRatio: string;
  languageCode: string | null;
}

export interface LocalizedMiniBanner {
  id: number;
  linkUrl: string | null;
  /**
   * Картинки баннера под нужный язык, по одной на aspectRatio.
   * Гарантированно содержит 19:2 (десктоп); 21:9 (мобильный) опционален.
   */
  images: LocalizedMiniBannerImage[];
  title: string | null;
  subtitle: string | null;
  buttonText: string | null;
}

// Teacher Service & Repository Types
export interface LocalizedTeacher {
  id: number;
  photoUrl: string | null;
  photoId: number | null;
  name: string;
  role: string | null;
}

export interface TeacherWithCount {
  id: number;
  userId: number | null;
  photoUrl: string | null;
  photoId: number | null;
  socialLinks: Record<string, string> | null;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count: {
    courses: number;
    posts: number;
  };
}

export interface TeacherWithTranslations extends TeacherWithCount {
  translations: {
    languageCode: string;
    name: string;
    role: string | null;
    bio: string | null;
  }[];
}

// Cache Service Types
export interface CacheOptions {
  ttl?: number;
  prefix?: string;
}

// Markdown Service Types
export interface MarkdownOptions {
  sanitize?: boolean;
  breaks?: boolean;
  gfm?: boolean;
}
